"use client"

import type React from "react"
import { Card, CardBody, CardFooter, Col, Form, FormGroup, Input, Label, Row } from "reactstrap"
import { Btn } from "../../../../../AbstractElements"
import { useState, useEffect } from "react"
import {
  ProfileService,
  type UserProfile,
  type IdDocumentType,
  type UpdateProfileData,
} from "../../../../../Service/ProfileService"
import { toast } from "react-toastify"
import CommonCardHeader from "../../../../../CommonElements/CommonCardHeader/CommonCardHeader"
import Swal from "sweetalert2"

const EditProfileForm = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [idDocTypes, setIdDocTypes] = useState<IdDocumentType[]>([])
  const [formData, setFormData] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    phone: "",
    id_doc_type: 0,
    id_doc_number: "",
  })
  const [idDocFile, setIdDocFile] = useState<File | null>(null)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

  // Fetch profile data and ID document types - ONLY ONCE
  useEffect(() => {
    if (hasLoadedOnce) return

    setHasLoadedOnce(true)

    const fetchData = async () => {
      try {
        setLoading(true)
        console.log("Loading profile data for edit form...")

        const [profileData, docTypes] = await Promise.all([
          ProfileService.getProfile(),
          ProfileService.getIdDocumentTypes(),
        ])

        setProfile(profileData)
        setIdDocTypes(docTypes)

        if (profileData.person) {
          setFormData({
            first_name: profileData.person.first_name || "",
            middle_name: profileData.person.middle_name || "",
            last_name: profileData.person.last_name || "",
            phone: profileData.person.phone || "",
            id_doc_type: profileData.person.id_doc_type || 0,
            id_doc_number: profileData.person.id_doc_number || "",
          })
        }

        console.log("Profile data loaded for edit form")
      } catch (error) {
        console.error("Failed to fetch profile:", error)
        toast.error("Failed to load profile data")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: name === "id_doc_type" ? Number.parseInt(value) || 0 : value,
    }))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setIdDocFile(e.target.files[0])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!profile?.person) {
      await Swal.fire({
        title: "Error!",
        text: "Profile data not available. Please refresh the page and try again.",
        icon: "error",
        confirmButtonColor: "#dc3545",
      })
      return
    }

    try {
      setSaving(true)

      let updateData: UpdateProfileData = { ...formData }

      if (idDocFile) {
        const base64 = await ProfileService.fileToBase64(idDocFile)
        updateData = {
          ...updateData,
          id_doc_base64: base64,
        }
      }

      const response = await ProfileService.updateProfile(updateData)
      setProfile(response.profile)

      // Notify other components
      const event = new CustomEvent("profileUpdated")
      window.dispatchEvent(event)

      // Clear file input
      setIdDocFile(null)
      const fileInput = document.getElementById("id_doc_file") as HTMLInputElement
      if (fileInput) fileInput.value = ""

      // Show success SweetAlert with refresh option
      const result = await Swal.fire({
        title: "Success!",
        text: "Your profile has been updated successfully.",
        icon: "success",
        confirmButtonColor: "#198754",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showClass: {
          popup: "animate__animated animate__fadeInDown",
        },
        hideClass: {
          popup: "animate__animated animate__fadeOutUp",
        },
      })

      // Refresh page when user clicks OK
      if (result.isConfirmed) {
        window.location.reload()
      }
    } catch (error: any) {
      console.error("Failed to update profile:", error)

      // Handle validation errors with SweetAlert
      if (error.message.includes("Validation failed")) {
        await Swal.fire({
          title: "Validation Error!",
          html: error.message.replace(/\n/g, "<br>"),
          icon: "warning",
          confirmButtonColor: "#ffc107",
          confirmButtonText: "Fix Issues",
        })
      } else {
        // General error with SweetAlert
        await Swal.fire({
          title: "Update Failed!",
          text: error.response?.data?.error || error.message || "Failed to update profile. Please try again.",
          icon: "error",
          confirmButtonColor: "#dc3545",
          confirmButtonText: "Try Again",
        })
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Col xl="8">
        <Card>
          <CardBody className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading profile...</p>
          </CardBody>
        </Card>
      </Col>
    )
  }

  if (!profile?.person) {
    return (
      <Col xl="8">
        <Card>
          <CardBody className="text-center py-5">
            <h4 className="text-muted">Profile not found</h4>
            <p>Unable to load profile information.</p>
            <button className="btn btn-primary mt-3" onClick={() => window.location.reload()}>
              Refresh Page
            </button>
          </CardBody>
        </Card>
      </Col>
    )
  }

  return (
    <Col xl="8">
      <Form onSubmit={handleSubmit}>
        <Card>
          <CommonCardHeader title="Edit Profile" tagClass="card-title mb-0" />
          <CardBody>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>First Name *</Label>
                  <Input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Middle Name</Label>
                  <Input
                    type="text"
                    name="middle_name"
                    value={formData.middle_name}
                    onChange={handleInputChange}
                    placeholder="Enter middle name (optional)"
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Last Name *</Label>
                  <Input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Email Address</Label>
                  <Input type="email" value={profile.email} disabled className="bg-light text-dark" />
                  <small className="text-muted">Email cannot be changed</small>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Full Name</Label>
                  <Input
                    type="text"
                    value={`${formData.first_name} ${formData.middle_name} ${formData.last_name}`
                      .replace(/\s+/g, " ")
                      .trim()}
                    disabled
                    className="bg-success"
                  />
                  <small className="text-muted">Auto-generated from first, middle, and last name</small>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>ID Document Type *</Label>
                  <Input
                    type="select"
                    name="id_doc_type"
                    value={formData.id_doc_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select ID Document Type</option>
                    {idDocTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>ID Document Number *</Label>
                  <Input
                    type="text"
                    name="id_doc_number"
                    value={formData.id_doc_number}
                    onChange={handleInputChange}
                    placeholder="Enter ID document number"
                    required
                  />
                  {formData.id_doc_type && idDocTypes.find((t) => t.id === formData.id_doc_type)?.length && (
                    <small className="text-muted">
                      Required length: {idDocTypes.find((t) => t.id === formData.id_doc_type)?.length} characters
                    </small>
                  )}
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>ID Document Attachment</Label>
                  <Input type="file" id="id_doc_file" accept="image/*,.pdf" onChange={handleFileChange} />
                  <small className="text-muted">Upload a clear photo or scan of your ID document (JPG, PNG, PDF)</small>
                  {profile.person.id_doc_attachment && (
                    <div className="mt-2">
                      <small className="text-success">
                        ✓ Current document:{" "}
                        <a href={profile.person.id_doc_attachment} target="_blank" rel="noopener noreferrer">
                          View Document
                        </a>
                      </small>
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
          <CardFooter className="text-end">
            <Btn color="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Updating...
                </>
              ) : (
                "Update Profile"
              )}
            </Btn>
          </CardFooter>
        </Card>
      </Form>
    </Col>
  )
}

export default EditProfileForm
